import { useEffect, useMemo, useRef, useState } from "react";
import { activeCameraIdAtom } from "@/atoms/activeCamera";
import { activeRegionAtom } from "@/atoms/activeRegion";
import { unitSystemAtom } from "@/atoms/units";
import {
  getDirectionLabel,
  useNearestBuoyData,
  getLatestReading,
  STALE_READING_MINUTES,
} from "@/lib/buoys";
import {
  buildForecastSnapshots,
  LEAD_TIME_BUCKETS,
  logForecastSnapshot,
  type ForecastSnapshot,
} from "@/lib/forecast-accuracy";
import { describeConditions, descriptorSeed, rateSurfQuality } from "@/lib/conditions";
import { useRegion, getCamerasFromRegion, getBreakFromRegion } from "@/hooks/useRegions";
import { selectDisplayedSwell } from "@/lib/displayedSwell";
import { useMarineData, type BreakType, type HourlyMarine } from "@/lib/marine";
import { tideFactor } from "@/lib/nearshore";
import type { BreakCalibration } from "@/lib/regions";
import { fontStack } from "@/theme";
import { formatHeight, formatTemp, formatWindSpeed } from "@/lib/units";
import {
  findNearestWind,
  getWeatherCodeLabel,
  useOpenMeteoWeather,
  type HourlyWind,
} from "@/lib/weather";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
  useTheme,
} from "@mui/material";
import { parseISO, format } from "date-fns";
import { useRecoilValue } from "recoil";
import ConditionsHeadline from "./ConditionsHeadline";
import ConditionFeedback from "./ConditionFeedback";
import ForecastChart from "./ForecastChart";
import ForecastDetailPanel, { tideAtTime } from "./ForecastDetailPanel";
import HourlyForecastTable from "./HourlyForecastTable";
import TideChart from "./TideChart";
import { useTideData } from "@/lib/tides";

const FORECAST_CHALLENGERS_REQUESTED =
  import.meta.env.VITE_FORECAST_CHALLENGERS_ENABLED === "1" ||
  import.meta.env.VITE_FORECAST_CHALLENGERS_ENABLED === "true";

export default function CompactForecast() {
  const activeRegion = useRecoilValue(activeRegionAtom);
  const activeCameraId = useRecoilValue(activeCameraIdAtom);
  const { data: regionData } = useRegion(activeRegion);

  if (!regionData || !activeRegion) return null;

  const firstBreak = regionData.breaks[0];
  if (!firstBreak) return null;

  // Use per-break coordinates when a specific break is active
  const activeBreak = activeCameraId
    ? getBreakFromRegion(regionData, activeCameraId)
    : null;

  return (
    <ForecastContent
      lat={activeBreak?.lat ?? firstBreak.lat}
      lon={activeBreak?.lon ?? firstBreak.lon}
      label={regionData.label}
      region={activeRegion}
      breakId={activeBreak?.id ?? firstBreak.id}
      shoreNormalOverride={activeBreak?.shoreNormal ?? firstBreak.shoreNormal}
      breakTypeOverride={activeBreak?.breakType ?? firstBreak.breakType ?? "beach"}
      breakCalibrationOverride={
        activeBreak?.calibration ?? firstBreak.calibration ?? null
      }
    />
  );
}

interface ForecastContentProps {
  lat: number;
  lon: number;
  label: string;
  region: string;
  breakId: string;
  shoreNormalOverride?: number | null;
  breakTypeOverride?: BreakType | null;
  breakCalibrationOverride?: BreakCalibration | null;
}

function ForecastContent({
  lat,
  lon,
  label,
  region,
  breakId,
  shoreNormalOverride,
  breakTypeOverride,
  breakCalibrationOverride,
}: ForecastContentProps) {
  const units = useRecoilValue(unitSystemAtom);
  const activeCameraId = useRecoilValue(activeCameraIdAtom);

  // Resolve active camera name for the section title
  const { data: regionData } = useRegion(region);
  const cameras = regionData ? getCamerasFromRegion(regionData) : [];
  const activeCamera = activeCameraId
    ? cameras.find((c) => c.id === activeCameraId)
    : cameras[0];
  const cameraLabel = activeCamera?.name ?? label;

  const marine = useMarineData(lat, lon, breakTypeOverride ?? "beach", {
    includeModels: FORECAST_CHALLENGERS_REQUESTED,
    // Passing regionId opts this fetch into server-side bias correction
    // (gated by FORECAST_CORRECTION_LIVE_ENABLED). The skill rollup is
    // per-region, not per-coordinate.
    regionId: region,
  });
  const weather = useOpenMeteoWeather(lat, lon);
  const tides = useTideData(lat, lon, region);
  const buoys = useNearestBuoyData(lat, lon);

  // Resample the marine forecast at the buoy's coordinate so the 0h
  // accuracy comparison is apples-to-apples. Before, we logged
  // forecast(region) vs observation(buoy) — a mix of model error and
  // spatial offset (buoys can sit 20+km from the break in deeper water).
  // Open-Meteo caches aggressively and React Query dedupes by (lat,lon),
  // so the extra call is effectively free on repeat visits.
  const buoyStation = buoys.nearshore?.station ?? null;
  const buoyMarine = useMarineData(
    buoyStation?.lat ?? null,
    buoyStation?.lon ?? null,
    "beach",
    {
      includeModels: FORECAST_CHALLENGERS_REQUESTED,
      regionId: region,
    },
  );

  // Log forecast vs observation for accuracy tracking. We emit one row
  // per canonical lead time (0h/6h/24h/48h/72h) so the dashboard can
  // bucket MAE by how far ahead we were predicting.
  //
  // The 0h row carries a buoy observation; when we have one, we use the
  // forecast values resampled at the *buoy's* coordinates (not the
  // region's), so forecast and observation are compared at the same
  // point. Longer-lead rows stay at region coordinates — those are
  // forecast-only and are about the user's target spot.
  const loggedRef = useRef<string | null>(null);
  const m = marine.data;
  const bm = buoyMarine.data;
  const w = weather.data;
  const breakCalibrationKey = JSON.stringify(breakCalibrationOverride ?? null);
  useEffect(() => {
    if (!m || !region || !w) return;
    if (buoyStation && !bm) return;
    if (buoyStation && buoys.nearshore?.isLoading) return;
    if (FORECAST_CHALLENGERS_REQUESTED && tides.isLoading) return;

    const buoyReading = buoys.nearshore?.data
      ? getLatestReading(buoys.nearshore.data, STALE_READING_MINUTES)
      : null;
    const issueTime = new Date().toISOString();
    const forecastTideAt = (validTime: string) => {
      const tide = tideAtTime(tides.data, validTime);
      if (!tide) return null;
      return {
        heightM: tide.height,
        trend: tide.trend === "steady" ? ("slack" as const) : tide.trend,
      };
    };

    const source = bm ?? m;
    const firstHourly = source.hourly?.[0]?.time ?? "";
    const observationKey =
      buoyReading?.waveHeight != null ? "with-observation" : "no-observation";
    const tideKey = FORECAST_CHALLENGERS_REQUESTED
      ? (tides.data?.stationId ?? (tides.isError ? "tide-error" : "no-tide"))
      : "spot-off";
    const key = `${region}-${breakId}-${buoyStation?.id ?? "no-buoy"}-${source.offshoreHeightM}-${firstHourly}-${w.currentWindSpeed}-${w.currentWindDirection}-${w.currentWindGusts ?? "no-gust"}-${observationKey}-${tideKey}-${breakCalibrationKey}`;
    if (loggedRef.current === key) return;

    const snapshots: ForecastSnapshot[] = buildForecastSnapshots({
      regionId: region,
      targetLat: lat,
      targetLon: lon,
      issueTime,
      marine: m,
      forecastWindSpeedKmh: w.currentWindSpeed,
      forecastWindDirectionDeg: w.currentWindDirection,
      forecastWindGustKmh: w.currentWindGusts,
      breakId,
      shoreNormal: shoreNormalOverride,
      breakType: breakTypeOverride,
      buoyStation,
      buoyMarine: bm,
      rawModels: m.models,
      buoyRawModels: bm?.models,
      buoyReading,
      includeRawModelSnapshots:
        FORECAST_CHALLENGERS_REQUESTED && source.modelTelemetryEnabled === true,
      includeSpotCalibratedSnapshots: FORECAST_CHALLENGERS_REQUESTED,
      spotCalibration: breakCalibrationOverride,
      forecastTideAt: FORECAST_CHALLENGERS_REQUESTED ? forecastTideAt : undefined,
      leadBuckets: LEAD_TIME_BUCKETS,
    });

    if (snapshots.length > 0) {
      loggedRef.current = key;
      logForecastSnapshot(snapshots);
    }
    // `buoyStation` is referenced via its stable primitive fields
    // (id/lat/lon) to avoid retriggering on every render when the
    // object reference changes but its values don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    m,
    bm,
    w,
    buoys.nearshore?.data,
    region,
    lat,
    lon,
    breakId,
    shoreNormalOverride,
    breakTypeOverride,
    breakCalibrationKey,
    buoyStation?.id,
    buoyStation?.lat,
    buoyStation?.lon,
    tides.data,
    tides.isError,
    tides.isLoading,
  ]);

  const isLoading = marine.isLoading || weather.isLoading;
  const isError = marine.isError && weather.isError;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: "text.secondary" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", py: 2 }}
      >
        Unable to load conditions data.
      </Typography>
    );
  }

  const shoreNormal = shoreNormalOverride ?? regionData?.breaks[0]?.shoreNormal ?? null;
  const currentTide = tideAtTime(tides.data, new Date().toISOString());
  const currentTideQualityFactor =
    breakCalibrationOverride && currentTide
      ? tideFactor(currentTide.height, currentTide.trend, breakCalibrationOverride)
      : null;

  const ratingInput = m
    ? {
        waveHeightFt: m.waveHeightM * 3.28084,
        period: m.wavePeriod,
        windSpeedKmh: w?.currentWindSpeed ?? null,
        windGustKmh: w?.currentWindGusts ?? null,
        windDirectionDeg: w?.currentWindDirection ?? null,
        swellDirectionDeg: m.swellDirection ?? m.waveDirection,
        shoreNormal,
        tideQualityFactor: currentTideQualityFactor,
      }
    : null;
  const qualityResult = ratingInput ? rateSurfQuality(ratingInput) : null;
  const rating = qualityResult?.rating ?? null;
  // Seed by spot + hour-bucket so the phrase varies across sessions and
  // spots, but stays stable within a render cycle and for ~an hour
  // afterwards (no flicker between re-renders).
  const hourBucket = Math.floor(Date.now() / (60 * 60 * 1000));
  const seed = descriptorSeed(region, activeCameraId ?? "", rating ?? "", hourBucket);
  const descriptor =
    rating && ratingInput ? describeConditions(rating, ratingInput, seed) : null;
  const currentSwell = m
    ? selectDisplayedSwell({
        swellHeightM: m.swellHeightM,
        swellPeriodS: m.swellPeriod,
        swellDirectionDeg: m.swellDirection,
        waveHeightM: m.offshoreHeightM,
        wavePeriodS: m.wavePeriod,
        waveDirectionDeg: m.waveDirection,
      })
    : null;

  return (
    <Box sx={{ mt: { xs: 1.5, sm: 4 } }}>
      {m && rating && descriptor && (
        <Box sx={{ mb: 3 }}>
          <ConditionsHeadline
            spotLabel={cameraLabel}
            waveHeightM={m.waveHeightM}
            rating={rating}
            descriptor={descriptor}
            dataUpdatedAt={marine.dataUpdatedAt}
            units={units}
          />
          <ConditionFeedback regionId={region} breakId={breakId} />
        </Box>
      )}

      {/* Readout strip — no card, no border. Hairline rules separate stats
          on desktop; horizontal rules on mobile. The readout is the atomic
          unit per DESIGN.md — labeled mono number, nothing else.
          "Surf" = face height at the break; "Swell" = `selectDisplayedSwell`
          (primary swell, or significant wave height when that swell is
          missing or is short-period chop). Showing both aligns with how
          Surfline / Surf Captain present conditions and stops users reading
          an offshore 6.5ft as "surf". */}
      {m && currentSwell && (
        <Box
          sx={{
            mb: 3,
            py: 2,
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: `repeat(${3 + (m.waterTempC != null ? 1 : 0) + (w ? 2 : 0)}, 1fr)`,
            },
            columnGap: { xs: 2, md: 3 },
            rowGap: 2,
          }}
        >
          <Readout label="Surf" value={formatHeight(m.waveHeightM, units)} />
          <Readout
            label="Swell"
            value={
              currentSwell.heightM == null
                ? "—"
                : `${formatHeight(currentSwell.heightM, units)}${
                    currentSwell.periodS != null
                      ? ` @ ${currentSwell.periodS.toFixed(0)}s`
                      : ""
                  } ${getDirectionLabel(currentSwell.directionDeg)}`
            }
          />
          {m.waterTempC !== null && (
            <Readout label="Water" value={formatTemp(m.waterTempC, units)} />
          )}
          {w && (
            <>
              <Readout label="Air" value={formatTemp(w.currentTemp, units)} />
              <Readout
                label="Wind"
                value={formatWindWithGusts(w.currentWindSpeed, w.hourlyWind, units)}
              />
            </>
          )}
        </Box>
      )}

      {/* Next days — promoted above the chart because the most common
          thing a surfer is checking for is "is there going to be surf
          this week". The chart + hourly are drill-downs for when the
          answer to that is "yes, look closer". */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="overline"
          sx={{ color: "ink.muted", display: "block", mb: 1.5 }}
        >
          Next days
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          {m &&
            m.daily.map((day, idx) => {
              const dayWind = w?.daily[idx];
              // Suppress the weather label when it matches the previous
              // day's — "OVERCAST" repeating for five rows adds noise
              // without new information. Still show on the first day and
              // whenever the pattern changes so the reader can see it
              // break.
              const prevWind = idx > 0 ? w?.daily[idx - 1] : null;
              const weatherLabel =
                dayWind && dayWind.weatherCode !== prevWind?.weatherCode
                  ? getWeatherCodeLabel(dayWind.weatherCode)
                  : null;

              // Split the day into AM (06–11) and PM (12–17) windows and
              // take the peak surf face height in each from the hourly
              // series. Matches how Surf Captain splits sessions.
              const am = windowPeak(m.hourly, day.date, 6, 11);
              const pm = windowPeak(m.hourly, day.date, 12, 17);

              const rateSession = (
                peak: ReturnType<typeof windowPeak>,
              ): ReturnType<typeof rateSurfQuality>["rating"] | null => {
                if (!peak) return null;
                const sessionWind =
                  w?.hourlyWind && w.hourlyWind.length > 0
                    ? findNearestWind(peak.time, w.hourlyWind)
                    : null;
                const sessionTide = tideAtTime(tides.data, peak.time);
                return rateSurfQuality({
                  waveHeightFt: peak.heightM * 3.28084,
                  period: peak.period,
                  windSpeedKmh: sessionWind?.windSpeedKmh ?? null,
                  windGustKmh: sessionWind?.windGustsKmh ?? null,
                  windDirectionDeg: sessionWind?.windDirectionDeg ?? null,
                  swellDirectionDeg: peak.dir,
                  shoreNormal,
                  tideQualityFactor:
                    breakCalibrationOverride && sessionTide
                      ? tideFactor(
                          sessionTide.height,
                          sessionTide.trend,
                          breakCalibrationOverride,
                        )
                      : null,
                }).rating;
              };

              const heightLabel = (peak: ReturnType<typeof windowPeak>): string | null =>
                peak ? formatHeight(peak.heightM, units) : null;

              const periodLabel = (peak: ReturnType<typeof windowPeak>): string | null =>
                peak && peak.period != null ? `${peak.period.toFixed(0)}s` : null;

              return (
                <DailyRow
                  key={`day-${idx}`}
                  dateLabel={dailyDateLabel(day.date)}
                  sessions={[
                    {
                      label: "AM",
                      rating: rateSession(am),
                      height: heightLabel(am),
                      period: periodLabel(am),
                    },
                    {
                      label: "PM",
                      rating: rateSession(pm),
                      height: heightLabel(pm),
                      period: periodLabel(pm),
                    },
                  ]}
                  wind={
                    dayWind
                      ? `${formatWindSpeed(
                          dayWind.windSpeedMax,
                          units,
                        )} ${getDirectionLabel(dayWind.windDirectionDominant)}`
                      : null
                  }
                  airLow={dayWind ? formatTemp(dayWind.tempMin, units) : null}
                  airHigh={dayWind ? formatTemp(dayWind.tempMax, units) : null}
                  weatherLabel={weatherLabel}
                />
              );
            })}
        </Box>
      </Box>

      {/* 5-day forecast + detail panel */}
      {m && m.hourly.length > 0 && (
        <ForecastSection
          hourly={m.hourly}
          hourlyWind={w?.hourlyWind ?? []}
          tideData={tides.data}
          units={units}
          shoreNormal={shoreNormal}
        />
      )}

      {/* Tide chart */}
      {tides.data &&
        (tides.data.hourly.length > 0 || tides.data.predictions.length >= 2) && (
          <TideChart tideData={tides.data} />
        )}

      <Typography
        variant="overline"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 3,
          color: "ink.muted",
          fontSize: "0.625rem",
        }}
      >
        Data from Open-Meteo, NOAA &amp; NDBC
      </Typography>
    </Box>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        variant="overline"
        component="div"
        sx={{ color: "ink.muted", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: fontStack.mono,
          fontSize: { xs: "1.25rem", md: "1.5rem" },
          fontWeight: 500,
          color: "ink.main",
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Format current wind with a gust overlay when we have recent hourly
 * gust data — "12 mph · 18g" — matching the way Surfline and Surf
 * Captain surface gusts. Falls back to the mean wind alone when gusts
 * aren't present or are within a few mph of the mean (gusts that match
 * mean wind aren't informative).
 */
function formatWindWithGusts(
  currentKmh: number,
  hourlyWind: HourlyWind[] | undefined,
  units: "imperial" | "metric",
): string {
  const mean = formatWindSpeed(currentKmh, units);
  if (!hourlyWind || hourlyWind.length === 0) return mean;

  const now = Date.now();
  let closest: HourlyWind | null = null;
  let bestDiff = Infinity;
  for (const h of hourlyWind) {
    const diff = Math.abs(parseISO(h.time).getTime() - now);
    if (diff < bestDiff) {
      bestDiff = diff;
      closest = h;
    }
  }
  if (!closest || !closest.windGustsKmh) return mean;

  const gustDeltaKmh = closest.windGustsKmh - currentKmh;
  if (gustDeltaKmh < 3) return mean; // gust noise below ~2 mph isn't news

  const gustNumber =
    units === "imperial"
      ? (closest.windGustsKmh * 0.621371).toFixed(0)
      : closest.windGustsKmh.toFixed(0);
  // Spell out "gust" — "24g" was too cryptic when tested with users.
  return `${mean} · gust ${gustNumber}`;
}

/**
 * Peak surf face height during a sub-day window, taken from the hourly
 * series. Windows use local-time hours: AM = 06–11, PM = 12–17.
 * Returns null when no hourly sample falls in the window for that date.
 */
function windowPeak(
  hourly: HourlyMarine[],
  date: string,
  startHour: number,
  endHour: number,
): { time: string; heightM: number; period: number | null; dir: number } | null {
  let peak: { time: string; heightM: number; period: number | null; dir: number } | null =
    null;
  for (const h of hourly) {
    if (!h.time.startsWith(date)) continue;
    const parsed = parseISO(h.time);
    const hr = parsed.getHours();
    if (hr < startHour || hr > endHour) continue;
    if (!peak || h.waveHeightM > peak.heightM) {
      peak = {
        time: h.time,
        heightM: h.waveHeightM,
        period: h.wavePeriod,
        dir: h.swellDirection ?? h.waveDirection,
      };
    }
  }
  return peak;
}

interface DailySessionSummary {
  label: string;
  rating: ReturnType<typeof rateSurfQuality>["rating"] | null | undefined;
  height: string | null;
  period: string | null;
}

/**
 * One row of the daily forecast table with AM / PM surf face summaries.
 * Competitors split days for a reason — morning glass-off and afternoon
 * onshore are genuinely different surfs. Kelp/buoy text is reserved for
 * the extremes (firing / blown out) per the one-accent rule.
 */
function DailyRow({
  dateLabel,
  sessions,
  wind,
  airLow,
  airHigh,
  weatherLabel,
}: {
  dateLabel: string;
  sessions: DailySessionSummary[];
  wind: string | null;
  airLow: string | null;
  airHigh: string | null;
  weatherLabel: string | null;
}) {
  const theme = useTheme();
  const ratingToColor = (
    rating: ReturnType<typeof rateSurfQuality>["rating"] | null | undefined,
  ) =>
    rating === "EPIC" || rating === "GOOD"
      ? theme.palette.kelp.main
      : rating === "FLAT" || rating === "POOR"
        ? theme.palette.buoy.main
        : theme.palette.ink.main;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "auto 1fr",
          sm: "140px 1fr 1fr auto",
        },
        alignItems: "baseline",
        columnGap: 2,
        rowGap: 0.5,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: fontStack.serif,
            fontSize: "1rem",
            fontWeight: 500,
            color: "ink.main",
          }}
        >
          {dateLabel}
        </Typography>
        {weatherLabel && (
          <Typography variant="overline" sx={{ color: "ink.muted" }}>
            {weatherLabel}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}>
        {sessions.map((s) => (
          <Box key={s.label} sx={{ minWidth: 70 }}>
            <Typography variant="overline" sx={{ color: "ink.muted" }}>
              {s.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: fontStack.mono,
                fontSize: "0.9375rem",
                color: ratingToColor(s.rating),
                lineHeight: 1.2,
              }}
            >
              {s.height ?? "—"}
            </Typography>
            {s.period && (
              <Typography
                sx={{
                  fontFamily: fontStack.mono,
                  fontSize: "0.8125rem",
                  color: "ink.muted",
                  lineHeight: 1.2,
                }}
              >
                {s.period}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {wind && (
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: "0.9375rem",
            color: "ink.muted",
          }}
        >
          {wind}
        </Typography>
      )}

      {airLow && airHigh && (
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: "0.9375rem",
            color: "ink.muted",
            whiteSpace: "nowrap",
          }}
        >
          {airLow} / {airHigh}
        </Typography>
      )}
    </Box>
  );
}

interface ForecastSectionProps {
  hourly: HourlyMarine[];
  hourlyWind: HourlyWind[];
  tideData: ReturnType<typeof useTideData>["data"];
  units: "imperial" | "metric";
  shoreNormal: number | null;
}

function ForecastSection({
  hourly,
  hourlyWind,
  tideData,
  units,
  shoreNormal,
}: ForecastSectionProps) {
  // Initialise the detail panel to "now" — the hour closest to current time.
  // Falls back to 0 if the hourly series somehow starts in the future.
  const nowIdx = useMemo(() => {
    const nowMs = Date.now();
    let best = 0;
    let bestDiff = Infinity;
    hourly.forEach((h, i) => {
      const diff = Math.abs(parseISO(h.time).getTime() - nowMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    });
    return best;
  }, [hourly]);

  const [selectedIdx, setSelectedIdx] = useState<number>(nowIdx);
  // Default to open — the hourly table was the app's prior default view and
  // the "Show timeline" toggle was too quiet for users to discover. Keep the
  // toggle so anyone who wants the chart-only view can collapse it.
  const [timelineOpen, setTimelineOpen] = useState(true);

  // Re-anchor to "now" if the region/hourly series changes under us
  useEffect(() => {
    setSelectedIdx(nowIdx);
  }, [nowIdx]);

  const selected = hourly[selectedIdx] ?? null;
  const windByTime = useMemo(
    () => new Map(hourlyWind.map((h) => [h.time, h])),
    [hourlyWind],
  );
  const selectedWind = selected
    ? (windByTime.get(selected.time) ??
      (hourlyWind.length > 0 ? findNearestWind(selected.time, hourlyWind) : null))
    : null;
  const selectedTide = selected ? tideAtTime(tideData, selected.time) : null;

  return (
    <>
      <ForecastChart
        hourly={hourly}
        hourlyWind={hourlyWind}
        units={units}
        shoreNormal={shoreNormal}
        selectedIdx={selectedIdx}
        onSelect={setSelectedIdx}
      />

      {selected && (
        <ForecastDetailPanel
          marine={selected}
          wind={selectedWind}
          tide={selectedTide}
          units={units}
          shoreNormal={shoreNormal}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Button
          size="small"
          variant="text"
          onClick={() => setTimelineOpen((v) => !v)}
          sx={{
            color: "ink.muted",
            fontSize: "0.8125rem",
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            "&:hover": {
              color: "ink.main",
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {timelineOpen ? "Hide hourly" : "Show hourly"}
        </Button>
      </Box>

      <Collapse in={timelineOpen} timeout="auto" unmountOnExit>
        <HourlyForecastTable
          hourly={hourly}
          hourlyWind={hourlyWind}
          units={units}
          shoreNormal={shoreNormal}
        />
      </Collapse>
    </>
  );
}

/**
 * Only label "Today" when the date actually matches today in the
 * user's local timezone. Open-Meteo returns days in the queried spot's
 * local tz, so an Australia-based user viewing a Portugal spot can hit
 * a row whose date is "today in Portugal" but is yesterday or tomorrow
 * locally — blanket-labeling that "Today" is a lie.
 */
function dailyDateLabel(dateStr: string): string {
  const day = parseISO(dateStr);
  const now = new Date();
  const sameDay =
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate();
  return sameDay ? "Today" : format(day, "EEE, MMM d");
}
